import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PayPalButtons } from "@paypal/react-paypal-js";

interface SubscriptionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (credits: number) => void;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState<{ credits: number, price: string } | null>(null);

    if (!isOpen) return null;

    return (
        <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            <motion.div
                className="bg-white rounded-2xl max-w-md w-full p-8 text-center shadow-2xl overflow-y-auto max-h-[90vh]"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
            >
                <div className="text-6xl mb-4">✨</div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Top Up Try-On Credits</h2>
                <p className="text-gray-600 mb-6">
                    You've used all your free tries. Choose a package to continue trying on clothes:
                </p>

                <div className="space-y-4">
                    {isProcessing ? (
                        <div className="py-8">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                            <p className="mt-4 text-gray-600">Activating package...</p>
                        </div>
                    ) : selectedPackage ? (
                        <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
                            <div className="bg-purple-50 p-4 rounded-xl mb-4 border border-purple-100">
                                <p className="text-purple-800 font-bold text-lg">{selectedPackage.credits} Try-On Package</p>
                                <p className="text-purple-600 font-semibold">${selectedPackage.price}</p>
                            </div>
                            <PayPalButtons
                                style={{ layout: "vertical" }}
                                createOrder={(data, actions) => {
                                    return actions.order.create({
                                        intent: "CAPTURE",
                                        purchase_units: [
                                            {
                                                description: `${selectedPackage.credits} Try-on Credits Package`,
                                                amount: {
                                                    currency_code: "USD",
                                                    value: selectedPackage.price,
                                                },
                                            },
                                        ],
                                        application_context: {
                                            shipping_preference: "NO_SHIPPING",
                                        }
                                    });
                                }}
                                onApprove={async (data, actions) => {
                                    if (actions.order) {
                                        await actions.order.capture();
                                        setIsProcessing(true);
                                        onSuccess(selectedPackage.credits);
                                        setSelectedPackage(null);
                                    }
                                }}
                            />
                            <button
                                onClick={() => setSelectedPackage(null)}
                                className="mt-4 text-sm text-gray-500 hover:text-gray-700 underline"
                            >
                                Change Package
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-4">
                            <button
                                onClick={() => setSelectedPackage({ credits: 30, price: "5.00" })}
                                className="group p-4 border-2 border-gray-100 rounded-2xl hover:border-purple-500 hover:bg-purple-50 transition-all text-left flex items-center justify-between"
                            >
                                <div>
                                    <h3 className="font-bold text-gray-800 text-lg">Starter Pack</h3>
                                    <p className="text-gray-600">30 Try-ons</p>
                                </div>
                                <div className="text-right">
                                    <span className="text-2xl font-bold text-purple-600">$5</span>
                                </div>
                            </button>

                            <button
                                onClick={() => setSelectedPackage({ credits: 100, price: "10.00" })}
                                className="group p-4 border-2 border-purple-200 bg-purple-50/50 rounded-2xl hover:border-purple-600 hover:bg-purple-50 transition-all text-left flex items-center justify-between relative overflow-hidden"
                            >
                                <div className="absolute top-0 right-0 bg-purple-600 text-white text-[10px] px-3 py-1 rounded-bl-lg font-bold">Best Value</div>
                                <div>
                                    <h3 className="font-bold text-gray-800 text-lg">Pro Pack</h3>
                                    <p className="text-gray-600">100 Try-ons</p>
                                </div>
                                <div className="text-right">
                                    <span className="text-2xl font-bold text-purple-600">$10</span>
                                </div>
                            </button>
                        </div>
                    )}

                    {!selectedPackage && (
                        <button
                            onClick={onClose}
                            className="w-full text-gray-400 py-2 hover:text-gray-600 transition-colors text-sm mt-4"
                        >
                            Maybe Later
                        </button>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
};

export default SubscriptionModal;
